import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../../api/axios';
import type { AddChannel, Channel } from '../../types/channel';
import type { ChannelsState } from '../../types/store';

const initialState: ChannelsState = {
  data: [],
  status: 'idle',
  error: null,
};

export const fetchChannels = createAsyncThunk('channels/fetchChannels', async () => {
  const response = await axios.get('channels?limit=1000');

  return response.data;
});

export const addChannel = createAsyncThunk(
  'channels/addChannel',
  async ({ data }: { data: AddChannel }, { rejectWithValue }) => {
    try {
      const response = await axios.post('channels', data);
      if (response.data.result) {
        return { data: response.data.result };
      }
      return { data: null };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const editChannel = createAsyncThunk(
  'channels/editChannel',
  async ({ data }: { data: Partial<Channel> }, { rejectWithValue }) => {
    try {
      const response = await axios.post('channels', data);
      if (response.data.result) {
        return { data: response.data.result };
      }
      return { data: null };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteChannel = createAsyncThunk(
  'channels/deleteChannel',
  async ({ data }: { data: Channel }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`channels?channel_id=${data.channel_id}`);
      return { data, response: response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const channelsSlice = createSlice({
  name: 'channels',
  initialState: initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      // fetch channels
      .addCase(fetchChannels.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        console.debug('Fetched channels: ', state.data);
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot fetch channels: ', state.error);
      })
      // add channel
      .addCase(addChannel.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addChannel.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data: Channel | null = action.payload.data;
        if (data) {
          state.data.push(data);
          console.debug('Channel added: ', data);
        }
      })
      .addCase(addChannel.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot add channel: ', state.error);
      })
      // edit channel
      .addCase(editChannel.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editChannel.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data: Channel | null = action.payload.data;
        if (!data) {
          return;
        }
        const index = state.data.findIndex((el) => el.channel_id === data.channel_id);
        if (index !== -1) {
          state.data[index] = data;
          console.debug('Channel edited: ', data);
        }
      })
      .addCase(editChannel.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot edit channel: ', state.error);
      })
      // delete channel
      .addCase(deleteChannel.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteChannel.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter((el) => el.channel_id !== action.payload.data.channel_id);
        console.debug('Channel deleted: ', action.payload.data);
      })
      .addCase(deleteChannel.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot delete channel: ', state.error);
      });
  },
});

export const channelsActions = channelsSlice.actions;

export default channelsSlice.reducer;
